import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../Layout/LanguageContext';
import { 
  FaAward,
  FaMountain
} from 'react-icons/fa';

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-light);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

const PhotoContainer = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    order: -1;
  }
`;

const ProfilePhoto = styled.div`
  width: 400px;
  height: 500px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 51, 102, 0.15);
  border: 4px solid var(--background-light);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
    opacity: 0.1;
    z-index: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 375px;
  }

  @media (max-width: 480px) {
    width: 250px;
    height: 312px;
  }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 51, 102, 0.8));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  text-align: center;
  z-index: 2;
`;

const LocationText = styled.p`
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BioTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  line-height: 1.2;
`;

const BioText = styled.div`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.1rem;

  p {
    margin-bottom: 1rem;
  }
`;

const CertificationsSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid var(--background-dark);
`;

const CertificationsTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const CertificationCard = styled.div`
  background: var(--background-color);
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid var(--background-dark);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 51, 102, 0.1);
    border-color: var(--accent-color);
  }
`;

const CertificationIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
  color: white;
  font-size: 1.2rem;
`;

const CertificationName = styled.p`
  font-weight: 600;
  color: var(--primary-color);
  margin: 0;
  font-size: 0.9rem;
`;

const CertificationIssuer = styled.p`
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: var(--accent-color);
  border-radius: 50%;
  opacity: 0.4;

  &:nth-child(1) { top: 15%; left: 10%; }
  &:nth-child(2) { top: 25%; right: 15%; }
  &:nth-child(3) { top: 75%; left: 20%; }
  &:nth-child(4) { top: 85%; right: 25%; }
`;

const aboutContent = {
  de: {
    title: 'Über mich',
    subtitle: 'Lernen Sie Ihren Experten aus den Bergen kennen',
    bioTitle: 'Ihr Experte aus den Bergen: Yannick Reinke',
    bioText: [
      'Als Master of Science im Maschinenbau mit dem Schwerpunkt Automatisierung bringe ich eine seltene Kombination aus technischem Tiefgang und prozessorientiertem Denken mit. Meine Leidenschaft ist es, komplexe Herausforderungen an der Schnittstelle von Daten und Prozessen zu lösen.',
      'Mit mehrjähriger Erfahrung in der Konzeption und Umsetzung von BI-, Automatisierungs- und KI-Projekten für namhafte Kunden wie Volkswagen und in der Industrie helfe ich Unternehmen, das volle Potenzial ihrer Daten auszuschöpfen. Mein Ansatz ist stets partnerschaftlich, pragmatisch und auf Ihren konkreten Erfolg ausgerichtet.'
    ],
    location: 'Sonthofen, Allgäu',
    certifications: {
      title: 'Zertifizierungen & Qualifikationen',
      items: [
        { name: 'Data Analyst', issuer: 'Databricks' },
        { name: 'Data Analyst', issuer: 'Microsoft' },
        { name: 'Analytics Engineer', issuer: 'Fabric' },
        { name: 'Professional Scrum Master', issuer: 'Scrum.org' },
        { name: 'Change Practitioner', issuer: 'Prosci' }
      ]
    }
  },
  en: {
    title: 'About Me',
    subtitle: 'Meet your expert from the mountains',
    bioTitle: 'Your Expert from the Mountains: Yannick Reinke',
    bioText: [
      'As a Master of Science in Mechanical Engineering with a focus on automation, I bring a rare combination of technical depth and process-oriented thinking. My passion is solving complex challenges at the intersection of data and processes.',
      'With years of experience in designing and implementing BI, automation, and AI projects for renowned clients like Volkswagen and in industry, I help companies unlock the full potential of their data. My approach is always partnership-based, pragmatic, and focused on your concrete success.'
    ],
    location: 'Sonthofen, Allgäu',
    certifications: {
      title: 'Certifications & Qualifications',
      items: [
        { name: 'Data Analyst', issuer: 'Databricks' },
        { name: 'Data Analyst', issuer: 'Microsoft' },
        { name: 'Analytics Engineer', issuer: 'Fabric' },
        { name: 'Professional Scrum Master', issuer: 'Scrum.org' },
        { name: 'Change Practitioner', issuer: 'Prosci' }
      ]
    }
  }
};

const About: React.FC = () => {
  const { language } = useLanguage();
  const t = aboutContent[language];
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <AboutSection id="about" ref={ref}>
      <Container>
        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <AboutContent
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <PhotoContainer variants={itemVariants}>
            <ProfilePhoto>
              <img 
                src="/images/FOTOREINKE.jpg" 
                alt="Yannick Reinke - IT-Beratung Reinke" 
              />
              <PhotoOverlay>
                <LocationText>
                  <FaMountain />
                  {t.location}
                </LocationText>
              </PhotoOverlay>
            </ProfilePhoto>
          </PhotoContainer>

          <TextContent>
            <motion.div variants={itemVariants}>
              <BioTitle>{t.bioTitle}</BioTitle>
              <BioText>
                {t.bioText.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </BioText>
            </motion.div>

            <motion.div variants={itemVariants}>
              <CertificationsSection>
                <CertificationsTitle>{t.certifications.title}</CertificationsTitle>
                <CertificationsGrid>
                  {t.certifications.items.map((cert, index) => (
                    <CertificationCard key={index}>
                      <CertificationIcon>
                        <FaAward />
                      </CertificationIcon>
                      <CertificationName>{cert.name}</CertificationName>
                      <CertificationIssuer>{cert.issuer}</CertificationIssuer>
                    </CertificationCard>
                  ))}
                </CertificationsGrid>
              </CertificationsSection>
            </motion.div>
          </TextContent>
        </AboutContent>

        <FloatingElement
          animate={{
            y: [0, -15, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 20, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, -10, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 25, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />
      </Container>
    </AboutSection>
  );
};

export default About; 